import React from "react";
import { Wand2 } from "lucide-react";
import { Text } from "@/components/Typography";

type AILoadingVariant = "default" | "purple" | "primaryGradient";

interface AILoadingAnimationProps {
  message?: string;
  className?: string;
  variant?: AILoadingVariant;
}

const variantColors: Record<
  AILoadingVariant,
  {
    icon: string;
    spark1: string;
    spark2: string;
    spark3: string;
    spark4: string;
    ring: string;
    dots: string;
  }
> = {
  default: {
    icon: "text-primary-500",
    spark1: "bg-primary-400",
    spark2: "bg-primary-300",
    spark3: "bg-primary-500",
    spark4: "bg-primary-200",
    ring: "border-primary-200",
    dots: "bg-primary-400",
  },
  purple: {
    icon: "text-purple-500",
    spark1: "bg-purple-400",
    spark2: "bg-purple-300",
    spark3: "bg-purple-500",
    spark4: "bg-purple-200",
    ring: "border-purple-300",
    dots: "bg-purple-400",
  },
  primaryGradient: {
    icon: "text-purple-500",
    spark1: "bg-purple-400",
    spark2: "bg-purple-300",
    spark3: "bg-purple-500",
    spark4: "bg-purple-200",
    ring: "border-purple-300",
    dots: "bg-purple-400",
  },
};

export const AILoadingAnimation: React.FC<AILoadingAnimationProps> = ({
  message = "Analyzing your goals and AI matching you with experts!",
  className = "min-h-screen bg-gray-50",
  variant = "default",
}) => {
  const colors = variantColors[variant];

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {/* AI Loading Animation */}
      <div className="flex flex-col items-center justify-center">
        <div className="relative">
          {/* Main AI Wand Icon */}
          <div className="relative">
            <Wand2
              size={44}
              className={colors.icon}
              style={{
                animation: "float 3s ease-in-out infinite",
              }}
            />

            {/* Sparkle Effects */}
            <div
              className={`absolute -top-1 -right-1 w-2 h-2 ${colors.spark1} rounded-full`}
              style={{
                animation: "sparkle 1.5s ease-in-out infinite",
              }}
            />
            <div
              className={`absolute top-0.5 -left-2 w-1.5 h-1.5 ${colors.spark2} rounded-full`}
              style={{
                animation: "sparkle 1.8s ease-in-out infinite 0.3s",
              }}
            />
            <div
              className={`absolute -bottom-0.5 right-1 w-2 h-2 ${colors.spark3} rounded-full`}
              style={{
                animation: "sparkle 1.2s ease-in-out infinite 0.6s",
              }}
            />
            <div
              className={`absolute bottom-2 -left-0.5 w-1 h-1 ${colors.spark4} rounded-full`}
              style={{
                animation: "sparkle 2s ease-in-out infinite 0.9s",
              }}
            />
          </div>

          {/* Glowing Ring */}
          <div
            className={`absolute inset-0 rounded-full border-2 ${colors.ring}`}
            style={{
              animation: "glow 2.5s ease-in-out infinite",
              transform: "scale(1.5)",
            }}
          />
        </div>

        {/* Loading Text at Bottom */}
        <div className="mt-8 text-center">
          <Text className="text-sm font-normal text-gray-500">{message}</Text>

          {/* Loading Dots */}
          <div className="flex space-x-1 mt-3 justify-center">
            <div
              className={`w-2 h-2 ${colors.dots} rounded-full`}
              style={{
                animation: "bounce 1.4s ease-in-out infinite both",
              }}
            />
            <div
              className={`w-2 h-2 ${colors.dots} rounded-full`}
              style={{
                animation: "bounce 1.4s ease-in-out infinite both 0.2s",
              }}
            />
            <div
              className={`w-2 h-2 ${colors.dots} rounded-full`}
              style={{
                animation: "bounce 1.4s ease-in-out infinite both 0.4s",
              }}
            />
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
          }
          
          @keyframes sparkle {
            0%, 100% { 
              opacity: 0; 
              transform: scale(0.5); 
            }
            50% { 
              opacity: 1; 
              transform: scale(1.2); 
            }
          }
          
          @keyframes glow {
            0%, 100% { 
              opacity: 0.3; 
              transform: scale(1.5); 
            }
            50% { 
              opacity: 0.8; 
              transform: scale(1.8); 
            }
          }
          
          @keyframes bounce {
            0%, 80%, 100% { 
              transform: scale(0); 
            } 
            40% { 
              transform: scale(1); 
            }
          }
        `,
        }}
      />
    </div>
  );
};
